import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, PhoneOff, Signal, Video, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui';
import { ROUTES } from '@/config/constants';
import { useVoice } from './VoiceProvider';

const POS_KEY = 'winr:voice-dock-pos';
/** Min. px of the panel kept on-screen so it can sit at any edge but never get
 *  lost — this is the only constraint, so it's draggable essentially anywhere. */
const KEEP_VISIBLE = 52;
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Persistent "Voice Connected" dock — the Discord-style panel that stays put
 * while you roam the rest of the site. Shows your pfp + the channel you're in
 * with quick mute / leave, and clicking it jumps back into the call view. The
 * whole panel is draggable (position remembered per browser); hidden while the
 * full voice stage is already on screen.
 */
export function VoiceDock() {
  const navigate = useNavigate();
  const { session, stageOpen, status, participants, muted, cameraOn, toggleMuted, leave } = useVoice();

  const nodeRef = useRef<HTMLDivElement>(null);
  const downRef = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null);
  const draggedRef = useRef(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Restore a saved position, clamped into the current viewport.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { x: number; y: number };
      const w = nodeRef.current?.offsetWidth ?? 280;
      const h = nodeRef.current?.offsetHeight ?? 120;
      setPos({
        x: clamp(saved.x, KEEP_VISIBLE - w, document.documentElement.clientWidth - KEEP_VISIBLE),
        y: clamp(saved.y, KEEP_VISIBLE - h, document.documentElement.clientHeight - KEEP_VISIBLE),
      });
    } catch {
      /* ignore malformed saved state */
    }
  }, [session]);

  // Drag the whole panel. Window-level listeners (rather than pointer capture)
  // keep the inner buttons clickable; a movement threshold distinguishes a drag
  // from a click, and a real drag swallows the trailing click.
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = downRef.current;
      const node = nodeRef.current;
      if (!d || !node) return;
      if (!draggedRef.current && Math.hypot(e.clientX - d.x, e.clientY - d.y) < DRAG_THRESHOLD) return;
      draggedRef.current = true;
      setDragging(true);
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      setPos({
        x: clamp(e.clientX - d.dx, KEEP_VISIBLE - node.offsetWidth, vw - KEEP_VISIBLE),
        y: clamp(e.clientY - d.dy, KEEP_VISIBLE - node.offsetHeight, vh - KEEP_VISIBLE),
      });
    };
    const up = () => {
      if (downRef.current && draggedRef.current && nodeRef.current) {
        const rect = nodeRef.current.getBoundingClientRect();
        try {
          localStorage.setItem(POS_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
        } catch {
          /* storage may be unavailable */
        }
        // Keep the flag set through the trailing click, then clear it.
        setTimeout(() => (draggedRef.current = false), 0);
      }
      downRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  if (!session || stageOpen) return null;

  const me = participants.find((p) => p.self);
  const others = participants.filter((p) => !p.self);
  const speaking = me?.speaking ?? false;
  const live = status === 'connected';

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0 || !nodeRef.current) return;
    const rect = nodeRef.current.getBoundingClientRect();
    downRef.current = { x: e.clientX, y: e.clientY, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
  };

  // After a drag, swallow the click so it doesn't trigger a button underneath.
  const onClickCapture = (e: ReactMouseEvent) => {
    if (draggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const returnToCall = () =>
    navigate(ROUTES.community(session.communityId), { state: { voiceChannelId: session.channelId } });

  return (
    <div
      ref={nodeRef}
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
      className={cn(
        'fixed z-[120] w-[280px] animate-cv2-fade-in select-none overflow-hidden rounded-2xl border border-white/[0.12]',
        'bg-[linear-gradient(160deg,rgba(255,255,255,0.10),rgba(255,255,255,0.02))]',
        'shadow-[0_18px_50px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.10)]',
        'backdrop-blur-2xl backdrop-saturate-[1.4]',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
        !pos && 'bottom-4 left-4',
      )}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      {/* Status strip — click to jump back into the call */}
      <button
        type="button"
        onClick={returnToCall}
        className="flex w-full cursor-pointer items-center gap-2 border-b border-white/[0.08] px-3.5 py-2 text-left"
      >
        <Signal size={14} strokeWidth={2.6} className={cn(live ? 'text-[#4ade80]' : 'text-amber-400')} />
        <span className="flex-1 truncate text-[12px] font-bold tracking-[0.2px] text-[#4ade80] drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
          {live ? 'Voice Connected' : 'Connecting…'}
        </span>
        <span className="flex items-center gap-1 text-white/55">
          <Volume2 size={13} strokeWidth={2.2} />
          <span className="max-w-[90px] truncate text-[11px] font-semibold">{session.channelName}</span>
        </span>
      </button>

      {/* Identity + controls */}
      <div className="flex items-center gap-3 px-3.5 py-3">
        <div className="relative shrink-0">
          <div
            className={cn(
              'rounded-full p-0.5 transition-shadow duration-200',
              speaking ? 'ring-2 ring-[#4ade80]' : 'ring-1 ring-white/15',
            )}
          >
            <Avatar src={session.self.avatarSrc} name={session.self.name} size={40} />
          </div>
          {others.length > 0 && (
            <span className="absolute -bottom-1 -right-1 grid h-5 min-w-[20px] place-items-center rounded-full border-2 border-black/40 bg-accent px-1 text-[10px] font-black text-black">
              +{others.length}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 truncate text-[13px] font-bold text-[#fafafa] drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
            {session.self.name}
            {cameraOn && <Video size={12} className="text-accent" />}
          </div>
          <div className="truncate text-[11px] font-medium text-white/55">
            {others.length > 0 ? `with ${others.length} other${others.length > 1 ? 's' : ''}` : 'Just you'}
          </div>
        </div>

        <button
          className={cn(
            'grid h-9 w-9 cursor-pointer place-items-center rounded-full border-0 bg-white/[0.08] text-white/85 transition-all hover:bg-white/[0.16] hover:text-white',
            muted && 'bg-[rgba(237,66,69,0.22)] text-[#ff8e90] hover:bg-[rgba(237,66,69,0.3)]',
          )}
          title={muted ? 'Unmute' : 'Mute'}
          aria-pressed={muted}
          onClick={toggleMuted}
        >
          {muted ? <MicOff size={17} /> : <Mic size={17} />}
        </button>
        <button
          className="grid h-9 w-9 cursor-pointer place-items-center rounded-full border-0 bg-[#ed4245] text-white shadow-[0_4px_14px_rgba(237,66,69,0.4)] transition-all hover:bg-[#f04747] active:scale-90"
          title="Disconnect"
          aria-label="Disconnect"
          onClick={leave}
        >
          <PhoneOff size={16} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
